import React, { useLayoutEffect, useState } from 'react';
import styled from 'styled-components';
import LogoUploader from './components/LogoUploader';
import CategoryEditor from './components/CategoryEditor';
import MenuPreview from './components/MenuPreview';
import { MenuData, CategoryData, MAX_SECTIONS, BANNER_COLORS } from './types';
import { PRODUCT_LINES } from './lib/catalog';
import { DEFAULT_OFFICE_NAME, FIT_MAX_SIZE, newSection, pdfFileName, refreshCategories, starterCategories } from './lib/menu';
import { LETTER_HEIGHT_INCHES, LETTER_HEIGHT_PX, LETTER_WIDTH_INCHES, LETTER_WIDTH_PX, whenRenderReady } from './lib/page';
import { useCatalog } from './lib/useCatalog';
import { useObjectUrl } from './lib/useObjectUrl';
import { DEFAULT_FONT, fontStack, isFontAvailable, MENU_FONTS, SERVED_DEFAULT_FONT } from './lib/fonts';
import {
  computePreviewScale,
  SPLIT_MEDIA,
  STACKED_MEDIA,
  useElementWidth,
  useMediaQuery,
  useWindowHeight,
} from './lib/layout';
import { isEmbedded, useHostResize, useHostViewport } from './lib/embed';
import wonderfulLogo from './wonderful-logo.webp';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';

// Framed by the store page: no header of our own (the store has one) and no
// minimum height, so the reported height is the real content height.
const embedded = isEmbedded();

const AppContainer = styled.div<{ $embedded: boolean }>`
  margin: 0;
  padding: 0;
  width: 100%;
  min-height: ${props => (props.$embedded ? '0' : '100vh')};
  background: #ddd;
`;

// Standalone visits (flavormenu.wonderfuldental.com) get a small brand bar.
const BrandBar = styled.header`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 20px;
  background: #fff;
  border-bottom: 1px solid #e5e5e5;

  h1 {
    margin: 0;
    font-family: 'LEMON MILK', 'Helvetica Now', 'Helvetica Neue', Arial, sans-serif;
    font-weight: 300;
    font-size: 20px;
    color: #000;
  }

  a {
    display: flex;
    align-items: center;
  }

  img {
    height: 28px;
    width: auto;
    display: block;
  }
`;

// Side by side from SPLIT_MIN_WIDTH up, stacked (editor, then preview) below.
// Nothing here fixes a height or hides overflow, so the page scrolls as one
// document instead of nesting scroll areas.
const MainContent = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 2fr) minmax(0, 3fr);
  align-items: start;
  width: 100%;
  background: #ddd;

  @media ${STACKED_MEDIA} {
    grid-template-columns: minmax(0, 1fr);
  }
`;

const EditorSide = styled.div`
  min-width: 0;
  padding: 8px 20px 20px;
  background: #f5f5f5;
  font-family: 'Helvetica Now', 'Helvetica Neue', Arial, sans-serif;

  h2, h3 {
    font-family: 'Helvetica Now', 'Helvetica Neue', Arial, sans-serif;
    font-weight: bold;
    margin-top: 8px;
    margin-bottom: 6px;
  }

  @media ${STACKED_MEDIA} {
    padding: 8px 16px 16px;
  }
`;

const PreviewSide = styled.section`
  min-width: 0;
  padding: 20px;
  background: #ddd;
  border-left: 1px solid #eee;
  font-family: 'Helvetica Now', 'Helvetica Neue', Arial, sans-serif;
  position: sticky;
  top: 0;

  h2, h3 {
    font-family: 'Helvetica Now', 'Helvetica Neue', Arial, sans-serif;
    font-weight: bold;
  }

  @media ${STACKED_MEDIA} {
    position: static;
    padding: 16px;
    border-left: none;
  }
`;

// Reserves exactly the scaled page's height and centers it.
const PreviewFrame = styled.div`
  position: relative;
  width: 100%;
  overflow: hidden;
`;

const PreviewPage = styled.div`
  position: absolute;
  top: 0;
`;

// Keeps Generate PDF in reach while the editor scrolls on narrow screens.
const MobileActions = styled.div`
  display: none;

  @media ${STACKED_MEDIA} {
    display: flex;
    gap: 12px;
    align-items: center;
    justify-content: space-between;
    position: sticky;
    bottom: 0;
    z-index: 5;
    margin: 16px -16px -16px;
    padding: 12px 16px;
    background: rgba(245, 245, 245, 0.96);
    border-top: 1px solid #ccc;

    a {
      color: #000;
      font-size: 15px;
    }
  }
`;

const ControlBox = styled.div`
  padding-top: 6px;
  padding-bottom: 6px;
  padding-left: 20px;
  padding-right: 20px;
  border-radius: 8px;
  margin-top: 0;
  margin-bottom: 20px;
`;

const SettingsTitle = styled.h3`
  margin-top: 0;
  margin-bottom: 6px;
  color: #333;
  font-family: 'LEMON MILK', sans-serif;
  font-weight: 300;
`;

const Settings = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 10px;
  margin-top: 0;
  margin-bottom: 6px;
`;

const Input = styled.input`
  width: 100%;
  padding: 8px;
  margin: 10px 0;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-family: 'Helvetica Now', sans-serif;
`;

const ColorControl = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 10px 0;

  label {
    min-width: 120px;
    font-family: 'Helvetica Now', sans-serif;
  }

  input[type="color"] {
    padding: 0;
    width: 50px;
    height: 30px;
  }
`;

const FontSizeControl = styled.div`
  display: flex;
  align-items: center;
  margin-top: 4px;
  margin-bottom: 4px;
  gap: 10px;

  label {
    min-width: 120px;
    font-family: 'Helvetica Now', sans-serif;
  }

  input {
    width: 60px;
  }
`;

const SliderControl = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 2px;
  margin-bottom: 2px;

  label {
    min-width: 50px;
    font-size: 13px;
    font-family: 'Helvetica Now', sans-serif;
  }

  input[type="range"] {
    flex: 1;
    margin: 0;
  }

  input[type="checkbox"] {
    margin: 0;
  }

  span {
    min-width: 40px;
    text-align: right;
    font-size: 13px;
    font-family: 'Helvetica Now', sans-serif;
  }
`;

const ColorPalettes = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 10px;
  margin-top: 4px;
  margin-bottom: 4px;
`;

const ColorPalette = styled.button<{ $selected: boolean }>`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  height: 30px;
  border: 2px solid ${props => props.$selected ? '#55B6E7' : '#ccc'};
  border-radius: 4px;
  overflow: hidden;
  cursor: pointer;
  padding: 0;
  background: none;
  font-family: 'Helvetica Now', sans-serif;

  &:hover {
    border-color: #55B6E7;
  }
`;

const ColorStrip = styled.div`
  width: 100%;
  height: 100%;
`;

const ShapeSelector = styled.div`
  display: flex;
  gap: 10px;
  margin-top: 4px;
  margin-bottom: 4px;
`;

const ShapeButton = styled.button<{ $selected: boolean }>`
  padding: 8px 16px;
  border: 2px solid ${props => props.$selected ? '#55B6E7' : '#ccc'};
  border-radius: 4px;
  background: white;
  cursor: pointer;
  flex: 1;
  font-family: 'Helvetica Now', sans-serif;

  &:hover {
    border-color: #55B6E7;
  }
`;

const FontSelect = styled.select`
  width: 100%;
  padding: 8px;
  margin: 10px 0;
  border: 1px solid #ccc;
  border-radius: 4px;
  font-size: 16px;
  font-weight: bold;
  font-family: 'Helvetica Now', sans-serif;
`;

const BannerControls = styled.div`
  margin-bottom: 0px;
`;

const PreviewHeader = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px 20px;
  margin-bottom: 20px;
  font-family: 'Helvetica Now', sans-serif;

  h2 {
    margin: 0;
    font-family: 'LEMON MILK', sans-serif;
    font-weight: 300;
  }
`;

const ErrorNote = styled.p`
  margin: 0 0 12px;
  color: #b00020;
  font-family: 'Helvetica Now', 'Helvetica Neue', Arial, sans-serif;
`;

const ActionButton = styled.button`
  cursor: pointer;
  position: relative;
  text-transform: uppercase;
  padding: 10px 2rem;
  font-size: 18px;
  background-color: #00cbff;
  color: #000;
  border: 3px solid black;
  border-radius: 100px;
  text-decoration: none;
  overflow: visible;
  font-family: 'LEMON MILK Medium', 'LEMON MILK', sans-serif;
  width: max-content;
  font-weight: 400;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  display: flex;
  justify-content: center;
  align-items: center;
  margin-left: 0;
  margin-right: 0;

  &:hover:not(:disabled),
  &:focus-visible {
    background: #caf0ff;
    transform: scale(1.1);
  }

  &:disabled {
    cursor: progress;
    opacity: 0.7;
  }
`;

// Add a flex row for the background decoration title and enable control
const BackgroundDecorationRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
`;

// flavor menu event type for GA4 tracking
type FlavorMenuEvent = {
  event: 'flavormenu_created';
};

// extending the Window object to support dataLayer
declare global {
  interface Window {
    dataLayer: FlavorMenuEvent[];
  }
}

// initializing dataLayer
window.dataLayer = window.dataLayer || [];

// event data to be send to GTM
const eventData: FlavorMenuEvent = {
  event: 'flavormenu_created',
};

const App: React.FC = () => {
  const { catalog } = useCatalog();

  // Starts with a few flavors in each section, so the preview is a real menu
  // from the first second; the office removes what it does not offer.
  const [menuData, setMenuData] = useState<MenuData>(() => ({
    officeName: DEFAULT_OFFICE_NAME,
    menuTitle: 'CHOOSE YOUR FLAVOR',
    categories: starterCategories(catalog),
    fontSize: {
      officeName: 81,
      title: 71,
      categoryTitle: 88,
      flavorName: 38
    },
    confetti: {
      enabled: true,
      density: 2.3,
      size: 92,
      shape: 'stars',
      colors: ['#FFD54F', '#FF8A80', '#82B1FF']
    },
    font: DEFAULT_FONT,
    flavorImageSize: 400,
    autoFit: true,
    flavorSpacing: -50,
    logoSize: 450,
    bannerStyle: 'classic',
    footerNote: ''
  }));

  // The catalog starts as the bundled snapshot and becomes the live store a
  // moment later: keep the flavors on the menu in step (names, art, and Flow
  // flavors dropping their Coming Soon tag once they are in the store).
  React.useEffect(() => {
    setMenuData(prev => {
      const categories = refreshCategories(prev.categories, catalog);
      return categories === prev.categories ? prev : { ...prev, categories };
    });
  }, [catalog]);

  const colorPalettes = [
    ['#FFD54F', '#FF8A80', '#82B1FF'],
    ['#FF9AA2', '#FFB7B2', '#FFDAC1'],
    ['#E2F0CB', '#B5EAD7', '#C7CEEA'],
    ['#FF6B6B', '#4ECDC4', '#45B7D1'],
    ['#96CEB4', '#FFEEAD', '#FF6F69'],
    ['#588C7E', '#F2E394', '#F2AE72']
  ];

  const handleLogoChange = (file: File | undefined) => {
    setMenuData(prev => ({
      ...prev,
      logo: file
    }));
  };

  const handleOfficeNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setMenuData(prev => ({
      ...prev,
      officeName: e.target.value
    }));
  };

  const handleMenuTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setMenuData(prev => ({
      ...prev,
      menuTitle: e.target.value
    }));
  };

  const handleFooterNoteChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setMenuData(prev => ({
      ...prev,
      footerNote: e.target.value
    }));
  };

  const handleCategoryChange = (index: number, category: CategoryData) => {
    setMenuData(prev => ({
      ...prev,
      categories: prev.categories.map((c, i) => i === index ? category : c)
    }));
  };

  const handleFontSizeChange = (key: keyof MenuData['fontSize'], value: string) => {
    const size = parseInt(value);
    if (!isNaN(size) && size > 0) {
      setMenuData(prev => ({
        ...prev,
        fontSize: {
          ...prev.fontSize,
          [key]: size
        }
      }));
    }
  };

  const handleAddSection = () => {
    setMenuData(prev => {
      if (prev.categories.length >= MAX_SECTIONS) return prev;
      const used = new Set(prev.categories.map(c => c.line));
      const line =
        PRODUCT_LINES.find(l => !used.has(l.id) && (catalog[l.id] || []).length > 0) ||
        PRODUCT_LINES.find(l => (catalog[l.id] || []).length > 0) ||
        PRODUCT_LINES[0];
      const color = BANNER_COLORS.find(c => !prev.categories.some(cat => cat.color === c)) || BANNER_COLORS[0];
      return {
        ...prev,
        categories: [...prev.categories, newSection(catalog, line.id, color)]
      };
    });
  };

  const handleRemoveSection = (index: number) => {
    setMenuData(prev =>
      prev.categories.length <= 1
        ? prev
        : { ...prev, categories: prev.categories.filter((_, i) => i !== index) }
    );
  };

  const handleConfettiChange = (key: keyof MenuData['confetti'], value: any) => {
    setMenuData(prev => ({
      ...prev,
      confetti: {
        ...prev.confetti,
        [key]: value
      }
    }));
  };

  // Fonts the menu can only use if this computer has them installed.
  const [missingFonts, setMissingFonts] = useState<string[]>([]);
  const fontChosen = React.useRef(false);
  React.useEffect(() => {
    let cancelled = false;
    const localFonts = MENU_FONTS.filter(font => font.localOnly);
    Promise.all(localFonts.map(font => isFontAvailable(font.value))).then(available => {
      if (cancelled) return;
      const missing = localFonts.filter((_, i) => !available[i]).map(font => font.value);
      setMissingFonts(missing);
      // Without Cooper Black the menu would print in a stand-in; start from a
      // served font instead, unless the office already picked one.
      if (!fontChosen.current && missing.includes(DEFAULT_FONT)) {
        setMenuData(prev => (prev.font === DEFAULT_FONT ? { ...prev, font: SERVED_DEFAULT_FONT } : prev));
      }
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const handleFontChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    fontChosen.current = true;
    setMenuData(prev => ({
      ...prev,
      font: e.target.value
    }));
  };

  // The art size Fit to page settled on, shown in the (disabled) size field.
  const [fittedSize, setFittedSize] = useState(400);

  const handleAutoFitChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const autoFit = e.target.checked;
    // Turning it off keeps the size it had, so nothing jumps.
    setMenuData(prev => ({ ...prev, autoFit, flavorImageSize: autoFit ? prev.flavorImageSize : fittedSize }));
  };

  const handleFlavorImageSizeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const size = Math.min(FIT_MAX_SIZE, parseInt(e.target.value));
    if (!isNaN(size) && size > 0) {
      setMenuData(prev => ({
        ...prev,
        flavorImageSize: size
      }));
    }
  };

  const handleFlavorSpacingChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const spacing = parseInt(e.target.value);
    setMenuData(prev => ({
      ...prev,
      flavorSpacing: spacing
    }));
  };

  const handleBannerStyleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setMenuData(prev => ({
      ...prev,
      bannerStyle: e.target.value as any
    }));
  };

  const handleLogoSizeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const size = parseInt(e.target.value);
    if (!isNaN(size) && size > 0) {
      setMenuData(prev => ({ ...prev, logoSize: size }));
    }
  };

  const renderColorPalette = (colors: string[], isSelected: boolean) => (
    <ColorPalette
      key={colors.join('-')}
      type="button"
      $selected={isSelected}
      aria-pressed={isSelected}
      aria-label={`Colors ${colors.join(', ')}`}
      onClick={() => handleConfettiChange('colors', colors)}
    >
      {colors.map((color, i) => (
        <ColorStrip key={i} style={{ backgroundColor: color }} />
      ))}
    </ColorPalette>
  );

  const logoUrl = useObjectUrl(menuData.logo);
  const previewRef = React.useRef<HTMLDivElement>(null);
  const previewFrameRef = React.useRef<HTMLDivElement>(null);
  const appRef = React.useRef<HTMLDivElement>(null);
  const mainRef = React.useRef<HTMLDivElement>(null);
  const previewSideRef = React.useRef<HTMLElement>(null);
  const previewFrameWidth = useElementWidth(previewFrameRef);
  const split = useMediaQuery(SPLIT_MEDIA);
  const windowHeight = useWindowHeight();
  const hostViewport = useHostViewport(embedded);
  useHostResize(appRef, embedded, split ? 'split' : 'stacked');

  // In a frame, window.innerHeight is the frame's height, which the host may
  // set from our own reported height; use the host's viewport when it sends
  // one, else a laptop-sized cap, so the preview never feeds its own size.
  const visibleHeight = embedded ? hostViewport?.height ?? Math.min(windowHeight, 1000) : windowHeight;
  // Header row (title + button) and the column's padding.
  const previewScale = computePreviewScale({
    width: previewFrameWidth,
    split,
    viewportHeight: visibleHeight,
    reserved: 120,
  });

  // When the host sizes the frame to our full height, the frame itself no
  // longer scrolls and position:sticky has nothing to stick to. If the host
  // reports where the frame is, move the preview down to stay in view.
  const [followOffset, setFollowOffset] = useState(0);
  useLayoutEffect(() => {
    const main = mainRef.current;
    const side = previewSideRef.current;
    const frameScrolls = document.documentElement.scrollHeight > window.innerHeight + 2;
    if (!embedded || !split || !hostViewport || !main || !side || frameScrolls) {
      setFollowOffset(0);
      return;
    }
    const mainTop = main.getBoundingClientRect().top + window.scrollY;
    const visibleTop = Math.max(0, -hostViewport.top - mainTop);
    const room = Math.max(0, main.offsetHeight - side.offsetHeight);
    setFollowOffset(Math.round(Math.min(visibleTop, room)));
  }, [hostViewport, split, previewScale, menuData]);

  const [pageOverflow, setPageOverflow] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [pdfError, setPdfError] = useState('');
  const generatingRef = React.useRef(false);

  const generatePDF = async () => {
    // A double click used to produce two downloads and two analytics events.
    if (!previewRef.current || generatingRef.current) return;
    generatingRef.current = true;
    setGenerating(true);
    setPdfError('');

    // Clone the preview at full size at the document's origin so the capture
    // starts at the page origin without disturbing the on-screen preview.
    // Absolute, not fixed: a fixed clone on a scrolled page was captured
    // half-blank (the editor's grey showed through the lower part of the PDF).
    const original = previewRef.current;
    const clone = original.cloneNode(true) as HTMLElement;
    clone.style.transform = 'none';
    clone.style.position = 'absolute';
    clone.style.left = '0';
    clone.style.top = '0';
    clone.style.margin = '0';
    clone.style.background = '#ffffff';
    clone.style.zIndex = '9999';
    clone.style.pointerEvents = 'none';
    clone.setAttribute('aria-hidden', 'true');
    // On-screen helpers such as the empty logo box do not print.
    clone.querySelectorAll('[data-preview-only]').forEach(el => el.remove());

    document.body.appendChild(clone);

    try {
      await whenRenderReady(clone);
      const canvas = await html2canvas(clone, {
        scale: 1,
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff',
        x: 0,
        y: 0,
        scrollX: 0,
        scrollY: 0,
        width: LETTER_WIDTH_PX,
        height: LETTER_HEIGHT_PX,
        windowWidth: LETTER_WIDTH_PX,
        windowHeight: LETTER_HEIGHT_PX,
      });

      // Convert canvas to high-quality JPEG to shrink final PDF size (much smaller than PNG)
      const imgData = canvas.toDataURL('image/jpeg', 0.9); // 0.9 quality keeps fidelity while reducing bytes

      const pdf = new jsPDF({ orientation: 'portrait', unit: 'in', format: 'letter' });
      const office = menuData.officeName.trim();
      pdf.setProperties({
        title: office && office !== DEFAULT_OFFICE_NAME ? `${office} Flavor Menu` : 'Flavor Menu',
        subject: 'Flavor menu for patients',
        creator: 'Wonderful Dental Flavor Menu Maker',
      });
      pdf.addImage(imgData, 'JPEG', 0, 0, LETTER_WIDTH_INCHES, LETTER_HEIGHT_INCHES, undefined, 'FAST');
      pdf.save(pdfFileName(menuData.officeName));

      // send event data to GTM
      window.dataLayer.push(eventData);
    } catch (error) {
      console.error('PDF generation failed', error);
      setPdfError('The PDF could not be made. Please try again.');
    } finally {
      // Clean up the cloned element
      document.body.removeChild(clone);
      generatingRef.current = false;
      setGenerating(false);
    }
  };

  return (
    <AppContainer ref={appRef} $embedded={embedded}>
      {!embedded && (
        <BrandBar>
          <h1>Flavor Menu Maker</h1>
          <a href="https://wonderfuldental.com/" target="_blank" rel="noopener noreferrer">
            <img src={wonderfulLogo} alt="Wonderful Dental" />
          </a>
        </BrandBar>
      )}
      <MainContent ref={mainRef}>
        <EditorSide>
          <ControlBox>
            <SettingsTitle>Basic Settings</SettingsTitle>
            <LogoUploader
              onLogoChange={handleLogoChange}
              currentLogo={logoUrl}
            />
            <Input
              type="text"
              aria-label="Office name"
              value={menuData.officeName}
              onChange={handleOfficeNameChange}
              placeholder="Your Office Name"
            />
            <Input
              type="text"
              aria-label="Menu title"
              value={menuData.menuTitle}
              onChange={handleMenuTitleChange}
              placeholder="Menu Title"
            />
            <Input
              type="text"
              aria-label="Footer note (optional)"
              value={menuData.footerNote || ''}
              onChange={handleFooterNoteChange}
              placeholder="Footer note (optional), e.g. Ask us about our flavors!"
              maxLength={120}
            />
            <FontSelect
              aria-label="Menu font"
              value={menuData.font}
              onChange={handleFontChange}
              style={{ fontFamily: fontStack(menuData.font) }}
            >
              {MENU_FONTS.map(font => (
                <option
                  key={font.value}
                  value={font.value}
                  style={{ fontFamily: font.stack }}
                >
                  {font.label}
                  {missingFonts.includes(font.value) ? ' (not installed on this computer)' : ''}
                </option>
              ))}
            </FontSelect>
            <FontSizeControl>
              <label htmlFor="logo-size">Logo Size:</label>
              <input id="logo-size"
                type="range"
                min="100"
                max="600"
                value={menuData.logoSize}
                onChange={handleLogoSizeChange}
              />
              <span>{menuData.logoSize}px</span>
            </FontSizeControl>
          </ControlBox>

          <ControlBox>
            <SettingsTitle>Sizes & Colors</SettingsTitle>
            <Settings>
              <FontSizeControl>
                <label htmlFor="size-office-name">Office Name:</label>
                <input id="size-office-name"
                  type="number"
                  value={menuData.fontSize.officeName}
                  onChange={(e) => handleFontSizeChange('officeName', e.target.value)}
                  min="1"
                />
                <span>pt</span>
              </FontSizeControl>
              
              <FontSizeControl>
                <label htmlFor="size-title">Title:</label>
                <input id="size-title"
                  type="number"
                  value={menuData.fontSize.title}
                  onChange={(e) => handleFontSizeChange('title', e.target.value)}
                  min="1"
                />
                <span>pt</span>
              </FontSizeControl>

              <FontSizeControl>
                <label htmlFor="size-banner-text">Banner Text:</label>
                <input id="size-banner-text"
                  type="number"
                  value={menuData.fontSize.categoryTitle}
                  onChange={(e) => handleFontSizeChange('categoryTitle', e.target.value)}
                  min="1"
                />
                <span>pt</span>
              </FontSizeControl>

              <FontSizeControl>
                <label htmlFor="size-flavor-names">Flavor Names:</label>
                <input id="size-flavor-names"
                  type="number"
                  value={menuData.fontSize.flavorName}
                  onChange={(e) => handleFontSizeChange('flavorName', e.target.value)}
                  min="1"
                />
                <span>pt</span>
              </FontSizeControl>

              <FontSizeControl>
                <label htmlFor="size-flavor-images">Flavor Images:</label>
                <input id="size-flavor-images"
                  type="number"
                  value={menuData.autoFit ? fittedSize : menuData.flavorImageSize}
                  onChange={handleFlavorImageSizeChange}
                  min="100"
                  max={FIT_MAX_SIZE}
                  disabled={menuData.autoFit}
                />
                <span>px</span>
              </FontSizeControl>

              <FontSizeControl>
                <label htmlFor="fit-to-page">Fit to page:</label>
                <input id="fit-to-page"
                  type="checkbox"
                  checked={!!menuData.autoFit}
                  onChange={handleAutoFitChange}
                  style={{ width: 'auto' }}
                />
              </FontSizeControl>

              <FontSizeControl>
                <label htmlFor="flavor-spacing">Flavor Spacing:</label>
                <input id="flavor-spacing"
                  type="range"
                  min="-200"
                  max="200"
                  value={menuData.flavorSpacing}
                  onChange={handleFlavorSpacingChange}
                />
                <span>{menuData.flavorSpacing}px</span>
              </FontSizeControl>

              <BannerControls>
                <ColorControl>
                  <label htmlFor="banner-style">Banner Style:</label>
                  <select id="banner-style" value={menuData.bannerStyle} onChange={handleBannerStyleChange} style={{ flex: 1 }}>
                    <option value="classic">Classic</option>
                    <option value="angled">Angled</option>
                    <option value="bubble">Bubble</option>
                  </select>
                </ColorControl>
              </BannerControls>
            </Settings>
          </ControlBox>

          <ControlBox>
            <BackgroundDecorationRow>
              <SettingsTitle style={{ marginBottom: 0 }}>Background Decoration</SettingsTitle>
              <SliderControl style={{ marginBottom: 0, marginTop: 0 }}>
                <label htmlFor="decoration-enabled" style={{ minWidth: 'auto' }}>Enable:</label>
                <input
                  id="decoration-enabled"
                  type="checkbox"
                  checked={menuData.confetti.enabled}
                  onChange={(e) => handleConfettiChange('enabled', e.target.checked)}
                />
              </SliderControl>
            </BackgroundDecorationRow>
            
            {menuData.confetti.enabled && (
              <>
                <Settings>
                  <SliderControl>
                    <label htmlFor="decoration-density">Density:</label>
                    <input id="decoration-density"
                      type="range"
                      min="0.5"
                      max="3"
                      step="0.1"
                      value={menuData.confetti.density}
                      onChange={(e) => handleConfettiChange('density', parseFloat(e.target.value))}
                    />
                    <span>{menuData.confetti.density.toFixed(1)}x</span>
                  </SliderControl>

                  <SliderControl>
                    <label htmlFor="decoration-size">Size:</label>
                    <input id="decoration-size"
                      type="range"
                      min="10"
                      max="200"
                      value={menuData.confetti.size}
                      onChange={(e) => handleConfettiChange('size', parseInt(e.target.value))}
                    />
                    <span>{menuData.confetti.size}px</span>
                  </SliderControl>
                </Settings>

                <div>
                  <span id="decoration-shape">Shape:</span>
                  <ShapeSelector role="group" aria-labelledby="decoration-shape">
                    <ShapeButton
                      type="button"
                      $selected={menuData.confetti.shape === 'stars'}
                      aria-pressed={menuData.confetti.shape === 'stars'}
                      onClick={() => handleConfettiChange('shape', 'stars')}
                    >
                      Stars
                    </ShapeButton>
                    <ShapeButton
                      type="button"
                      $selected={menuData.confetti.shape === 'dots'}
                      aria-pressed={menuData.confetti.shape === 'dots'}
                      onClick={() => handleConfettiChange('shape', 'dots')}
                    >
                      Dots
                    </ShapeButton>
                    <ShapeButton
                      type="button"
                      $selected={menuData.confetti.shape === 'hearts'}
                      aria-pressed={menuData.confetti.shape === 'hearts'}
                      onClick={() => handleConfettiChange('shape', 'hearts')}
                    >
                      Hearts
                    </ShapeButton>
                  </ShapeSelector>
                </div>

                <div>
                  <span id="decoration-palette">Color Palette:</span>
                  <ColorPalettes role="group" aria-labelledby="decoration-palette">
                    {colorPalettes.map(palette =>
                      renderColorPalette(
                        palette,
                        JSON.stringify(palette) === JSON.stringify(menuData.confetti.colors)
                      )
                    )}
                  </ColorPalettes>
                </div>
              </>
            )}
          </ControlBox>

          {menuData.categories.map((category, index) => (
            <CategoryEditor
              key={category.uid || index}
              category={category}
              onChange={(category) => handleCategoryChange(index, category)}
              index={index}
              catalog={catalog}
              onRemove={menuData.categories.length > 1 ? () => handleRemoveSection(index) : undefined}
            />
          ))}
          {menuData.categories.length < MAX_SECTIONS && (
            <ControlBox>
              <ActionButton type="button" onClick={handleAddSection}>
                Add a Product Section
              </ActionButton>
            </ControlBox>
          )}

          <MobileActions>
            <a href="#preview">See preview</a>
            <ActionButton type="button" onClick={generatePDF} disabled={generating} aria-busy={generating}>
              {generating ? 'Making PDF' : 'Generate PDF'}
            </ActionButton>
          </MobileActions>
        </EditorSide>

        <PreviewSide
          id="preview"
          aria-label="Live preview"
          ref={previewSideRef}
          style={followOffset ? { transform: `translateY(${followOffset}px)` } : undefined}
        >
          <PreviewHeader>
            <h2>Live Preview</h2>
            <ActionButton type="button" onClick={generatePDF} disabled={generating} aria-busy={generating}>
              {generating ? 'Making PDF' : 'Generate PDF'}
            </ActionButton>
          </PreviewHeader>
          {pdfError && <ErrorNote role="alert">{pdfError}</ErrorNote>}
          {pageOverflow && (
            <ErrorNote role="status">
              The menu is taller than the page, so the bottom will be cut off. Try smaller flavor images,
              fewer flavors or fewer sections.
            </ErrorNote>
          )}
          <PreviewFrame ref={previewFrameRef} style={{ height: Math.ceil(LETTER_HEIGHT_PX * previewScale) }}>
            <PreviewPage style={{ left: Math.max(0, (previewFrameWidth - LETTER_WIDTH_PX * previewScale) / 2) }}>
              <MenuPreview
                data={menuData}
                logoUrl={logoUrl}
                ref={previewRef}
                scale={previewScale}
                onOverflowChange={setPageOverflow}
                onFitChange={setFittedSize}
              />
            </PreviewPage>
          </PreviewFrame>
        </PreviewSide>
      </MainContent>
    </AppContainer>
  );
};

export default App; 
